import {
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import {
  MAT_MENU_DEFAULT_OPTIONS,
  MatMenuModule,
  MatMenuTrigger,
} from '@angular/material/menu';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { UserMailAccountContract } from '@app-types/api/user-mail-account';
import {
  CONNECTION_STATUS,
  CONNECTION_STATUS_INFO,
} from '@app-shared/constants/connection-status';
import { TranslateModule } from '@ngx-translate/core';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatIconModule } from '@angular/material/icon';
import { MailAccountFoldersComponent } from '../mail-account-folders/mail-account-folders.component';
import {
  EMAILS_MENU_SORT,
  EMAIL_QUERY_PARAM,
  STORAGE_NAMES,
} from '@app-shared/constants/constants';
import { GenerateMailAccountTreeService } from '@app-services/generate-mail-account-tree.service';
import { TreeItem } from '@app-types/tree.item';
import { MailFolderContract } from '@app-types/api/mail-folder';
import { decodeInfoMailAccount } from '@app-utils/search-params';
import { filter } from 'rxjs/operators';
import { IntervalService } from '@app-services/interval.service';
import { UserWorkspaceService } from '@app-services/user-workspace-service';
import { LoggingHandler } from '@app-services/errors/logging.service';
import { SearchInfo } from '@app-types/search';
import { ROUTES } from '@app-shared/constants/routes/fe.routes';
import { MatIconButton } from '@angular/material/button';
import { MenuOptions, MenuType } from '@app-types/common';
import { OrderDirection } from '@app-types/enums/order-direction';
import { LocalStorageService } from '@app-services/local-storage.service';
import { sortByDate, sortByEmailAlias } from '@app-utils/sortData';
import { TourAnchorMatMenuDirective } from 'ngx-ui-tour-md-menu';

const { EMAILS } = ROUTES;
const { SORT_EMAILS } = STORAGE_NAMES;

@Component({
  selector: 'app-emails-sidenav',
  imports: [
    TranslateModule,
    MatExpansionModule,
    MatTooltipModule,
    MatIconModule,
    MailAccountFoldersComponent,
    MatMenuModule,
    MatIconButton,
    TourAnchorMatMenuDirective,
  ],
  templateUrl: './emails-side-nav.component.html',
  styleUrls: ['./emails-side-nav.component.scss'],
  providers: [
    {
      provide: MAT_MENU_DEFAULT_OPTIONS,
      useValue: { overlayPanelClass: 'panelClassMenu' },
    },
  ],
})
export class EmailsSideNavComponent implements OnInit {
  getMailAccountsError: string;
  @Output() leftMenuOpen = new EventEmitter();
  @Input() settingRoute = '';
  @ViewChild(MatMenuTrigger, { static: true }) matMenuTrigger: MatMenuTrigger;

  public mailAccountId: number;
  public mailAccountIds: number[] = [];
  public isTreeLoaded = false;
  public hideNotification = true;
  menuTopLeftPosition = { x: '0', y: '0' };
  connectionStatusName = CONNECTION_STATUS;
  connectionStatusInfo = CONNECTION_STATUS_INFO;
  public openMailAccountId: number | null;
  tree: TreeItem<number, MailFolderContract>[] = [];
  infoOpenedMailAccount: SearchInfo;
  ordersSortMenu: MenuType = EMAILS_MENU_SORT;
  orderBy = EMAILS_MENU_SORT[0].id;
  orderDirectionEnum = OrderDirection;
  orderDirection = this.orderDirectionEnum.Ascending;

  constructor(
    protected route: ActivatedRoute,
    protected router: Router,
    private generateMailAccountTreeService: GenerateMailAccountTreeService,
    private intervalService: IntervalService,
    public userWorkspaceService: UserWorkspaceService,
    protected loggingHandler: LoggingHandler,
    private localStorageService: LocalStorageService
  ) {}

  async ngOnInit(): Promise<void> {
    const sort = this.localStorageService.getData(SORT_EMAILS);

    if (sort) {
      this.orderDirection = sort.orderDirection;
      this.orderBy = sort.orderBy;
    }
    this.sortMailAccounts();

    this.getMailAccountsError = this.userWorkspaceService.mailAccountsError;
    if (!this.userWorkspaceService.mailAccounts.length) {
      return;
    }

    this.setInfoOpenMAilAccount(window.location.href);

    this.mailAccountIds = this.userWorkspaceService.mailAccounts.map(
      e => e.mailAccountId
    );
    this.tree = this.generateMailAccountTreeService.buildFolderTree(
      this.userWorkspaceService.userFolders
    );
    this.isTreeLoaded = true;
    this.userWorkspaceService.getBadgesForMailAccounts(this.mailAccountIds);

    this.router.events
      .pipe(filter(event => event instanceof NavigationEnd))
      .subscribe(() => {
        this.setInfoOpenMAilAccount(window.location.href);
      });

    !!this.userWorkspaceService.mailAccounts.length &&
      this.intervalService.startInterval(
        'badgesRefreshInterval',
        () => {
          this.userWorkspaceService.getBadgesForMailAccounts(
            this.mailAccountIds
          );
          // this.openMailAccountId &&
          //   this.userWorkspaceService.refreshUnread({
          //     mailAccountId: this.openMailAccountId,
          //     updateBadges: false,
          //   });
        },
        120000
      );
  }

  setInfoOpenMAilAccount(url: string): void {
    const newUrl = new URL(url);
    if (newUrl.pathname.includes(EMAILS)) {
      const search = newUrl.searchParams.get(EMAIL_QUERY_PARAM) ?? '';
      this.infoOpenedMailAccount = decodeInfoMailAccount(search);
      this.hideNotification =
        this.infoOpenedMailAccount?.hideNotifications !== false;
    }
    this.openMailAccountId =
      this.infoOpenedMailAccount?.acc ??
      this.userWorkspaceService.mailAccounts?.[0]?.mailAccountId ??
      null;
  }

  getMailAccountTree(
    mailAccountId: number
  ): TreeItem<number, MailFolderContract> | undefined {
    return this.tree.find(({ key }) => key === mailAccountId);
  }

  public getBadge(mailAccount: UserMailAccountContract): number | null {
    return (
      this.userWorkspaceService.mailAccountBadges.find(
        e => e.mailAccountId === mailAccount.mailAccountId
      )?.badge || null
    );
  }

  public async opened(
    event: any,
    mailAccount: UserMailAccountContract
  ): Promise<void> {
    this.userWorkspaceService.isChangeAccount = true;
    this.openMailAccountId = mailAccount.mailAccountId;
    if (!mailAccount.firstFolder) {
      return;
    }
    const hideNotification =
      this.userWorkspaceService.hideNotifications === false;

    this.router.navigate([EMAILS], {
      queryParams: {
        [EMAIL_QUERY_PARAM]: hideNotification
          ? mailAccount.firstLinkNotification
          : mailAccount.firstLink,
      },
    });
    this.userWorkspaceService.refreshUnread({
      mailAccountId: mailAccount.mailAccountId,
    });
    this.userWorkspaceService.isChangeAccount = false;
  }

  async updateDataSideNav(): Promise<void> {
    await this.userWorkspaceService.loadFolders();
    this.tree = this.generateMailAccountTreeService.buildFolderTree(
      this.userWorkspaceService.userFolders
    );
    this.openMailAccountId &&
      (await this.userWorkspaceService.refreshUnread({
        mailAccountId: this.openMailAccountId,
      }));
  }

  onRightClick(event: MouseEvent, item: any): void {
    // preventDefault avoids to show the visualization of the right-click menu of the browser
    event.preventDefault();
    if (!this.settingRoute) {
      return;
    }
    // we record the mouse position in our object
    this.menuTopLeftPosition.x = event.clientX + 'px';
    this.menuTopLeftPosition.y = event.clientY + 'px';
    // we open the menu
    // we pass to the menu the information about our object
    this.matMenuTrigger.menuData = { item };

    // we open the menu
    this.matMenuTrigger.openMenu();
  }

  clickSettingMailAccount(item: any): void {
    this.router.navigate(
      [
        `settings/organization/mail-account/`,
        `${item?.mailAccountId}`,
        'general',
      ],
      {
        onSameUrlNavigation: 'reload',
      }
    );
  }

  public getTitle(mailAccount: UserMailAccountContract): string {
    return mailAccount.mailAccountName
      ? mailAccount.mailAccountName
      : (mailAccount.mailAccountEmail ?? '');
  }

  getColorText(mailAccountId: number): string {
    const dataBadges = this.userWorkspaceService.mailAccountBadges.find(
      el => el.mailAccountId === mailAccountId
    );
    return dataBadges?.connectionStatus
      ? this.connectionStatusName[dataBadges.connectionStatus].color
      : '';
  }

  getConnectionStatusTitle(mailAccountId: number): string {
    const dataBadges = this.userWorkspaceService.mailAccountBadges.find(
      el => el.mailAccountId === mailAccountId
    );
    return dataBadges?.connectionStatus
      ? this.connectionStatusName[dataBadges.connectionStatus].title
      : '';
  }

  isConnectionStatusInfo(mailAccountId: number): boolean {
    const dataBadges = this.userWorkspaceService.mailAccountBadges.find(
      el => el.mailAccountId === mailAccountId
    );
    return !!dataBadges?.connectionStatusInfo;
  }

  getConnectionStatusInfo(mailAccountId: number): string {
    const dataBadges = this.userWorkspaceService.mailAccountBadges.find(
      el => el.mailAccountId === mailAccountId
    );
    return dataBadges?.connectionStatusInfo
      ? this.connectionStatusInfo[dataBadges.connectionStatusInfo]
      : '';
  }

  getConnectStatusInfo(mailAccountId: number): string {
    const dataBadges = this.userWorkspaceService.mailAccountBadges.find(
      el => el.mailAccountId === mailAccountId
    );
    return dataBadges?.connectionStatus
      ? this.connectionStatusName[dataBadges.connectionStatus].info
      : '';
  }

  sortMailAccounts(): void {
    this.localStorageService.setData(SORT_EMAILS, {
      orderBy: this.orderBy,
      orderDirection: this.orderDirection,
    });
    const newData =
      this.orderBy === EMAILS_MENU_SORT[1].id
        ? sortByDate(
            this.userWorkspaceService.mailAccounts,
            this.orderBy,
            this.orderDirection
          )
        : sortByEmailAlias(
            this.userWorkspaceService.mailAccounts,
            this.orderDirection
          );
    this.userWorkspaceService.mailAccounts = [...newData];
  }

  changeOrder(): void {
    this.orderDirection =
      this.orderDirection === this.orderDirectionEnum.Ascending
        ? this.orderDirectionEnum.Descending
        : this.orderDirectionEnum.Ascending;
    this.sortMailAccounts();
  }

  public setFilterChange(val: MenuOptions): void {
    this.orderBy = val.id;
    this.sortMailAccounts();
  }

  mailAccountTrackBy(index: number): number {
    return index;
  }
}
